import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import { handleChangeEventInput, unsavedChangesExist } from "helpers/Helpers";
import Input from "components/form/input/Input";
import TextField from "components/form/textField/TextField";
import { IAccommodationUnitType } from "./UnitTypes";
import {
  GetAccommodationUnitTypes,
  IPutAccommodationUnitType,
  IUnitTypeImage,
  PostAccommodationUnitTypeImage,
  PutAccommodationUnitType,
} from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import { GetAccommodationCategories } from "api/rpc/2024-04/facilityAdmin/accommodation/categories";
import { IAccommodationCategory } from "../categories/Categories";
import ImageUpload from "components/imageUpload/imageUpload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faXmark } from "@fortawesome/pro-regular-svg-icons";
import "./unitTypeEdit.scss";
import { GetAccommodationAmenities } from "api/rpc/2024-04/facilityAdmin/accommodation/amenities";
import Popup from "components/popup/Popup";
import { allIcons } from "components/IconSelector/iconSelector";

interface IAccommodationAmenity {
  id: number;
  title: string;
  icon: string;
}

export function UnitTypeEdit() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { typeId }: any = useParams();
  const amenitiesDropdownRef = useRef<HTMLDivElement>(null);

  const [unitType, setUnitType] = useState<
    IAccommodationUnitType & {
      description: string;
    }
  >({
    id: undefined,
    title: "",
    base_occupancy: undefined,
    max_occupancy: undefined,
    category_id: undefined,
    description: "",
    amenities: [],
  });

  const [unitTypeBeforeChanges, setUnitTypeBeforeChanges] = useState<
    IPutAccommodationUnitType & {
      description: string;
    }
  >(undefined);

  const [accommodationCategories, setAccommodationCategories] = useState(undefined);
  const [unitTypeImages, setUnitTypeImages] = useState<IUnitTypeImage[]>([]);
  const [accommodationAmenities, setAccommodationAmenities] = useState<IAccommodationAmenity[]>([]);

  const [filterState, setFilterState] = useState({
    search: "",
    selected: [],
    isDropdownOpen: false,
    deleteImageOpen: false,
  });

  useEffect(() => {
    void loadAccommodationAmenities();
    void loadAccommodationUnitType();
    void loadAccommodationCategories();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (amenitiesDropdownRef.current && !amenitiesDropdownRef.current.contains(event.target as Node)) {
        setFilterState(prev => ({ ...prev, isDropdownOpen: false }));
      }
    };

    if (filterState.isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterState.isDropdownOpen]);

  async function loadAccommodationUnitType() {
    if (unitType !== undefined) {
      setUnitType(undefined);
    }
    const accommodationUnitTypesRes = await GetAccommodationUnitTypes({ id: typeId }, false);

    console.log(accommodationUnitTypesRes);
    if (accommodationUnitTypesRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting accommodation unit types")));
      return;
    }

    const accommodationUnitType = accommodationUnitTypesRes?.data?.[0];

    console.log(accommodationUnitType);

    const newData = {
      id: accommodationUnitType?.id,
      title: accommodationUnitType?.title,
      base_occupancy: accommodationUnitType?.base_occupancy,
      max_occupancy: accommodationUnitType?.max_occupancy,
      category_id: accommodationUnitType?.category_id,
      description: "",
      amenities: accommodationUnitType?.amenities || [],
    };

    setUnitType(
      prevState => ({ ...(prevState || {}), ...newData } as IAccommodationUnitType & { description: string }),
    );
    setUnitTypeBeforeChanges(
      prevState => ({ ...(prevState || {}), ...newData } as IAccommodationUnitType & { description: string }),
    );

    if (accommodationUnitType?.image_url) {
      setUnitTypeImages([
        {
          id: accommodationUnitType.id,
          unit_type_id: accommodationUnitType.id,
          source: accommodationUnitType.image_url,
          path: accommodationUnitType.image_path,
          position: 0,
          width: 0,
          height: 0,
        },
      ]);
    } else {
      setUnitTypeImages([]);
    }
  }

  async function loadAccommodationAmenities() {
    if (accommodationAmenities !== undefined) {
      setAccommodationAmenities(undefined);
    }
    const accommodationAmenitiesRes = await GetAccommodationAmenities(null, false);

    if (accommodationAmenitiesRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading amenities"));

      setAccommodationAmenities([]);
      return;
    }

    setAccommodationAmenities(accommodationAmenitiesRes.data as IAccommodationAmenity[]);
  }

  async function updateAccommodationUnitType() {
    console.log(unitType);

    const unitTypeRes = await PutAccommodationUnitType(
      {
        id: typeId,
        title: unitType?.title,
        base_occupancy: unitType?.base_occupancy,
        max_occupancy: unitType?.max_occupancy,
        category_id: unitType?.category_id,
        amenity_ids: unitType?.amenities?.map((a: IAccommodationAmenity) => a.id),
      },

      true,
    );

    if (unitTypeRes.status !== StatusCode.OK) {
      dispatch(showError("Error saving unit type"));
      return;
    }

    const unit = Array.isArray(unitTypeRes?.data) ? unitTypeRes?.data?.[0] : unitTypeRes?.data;

    const updatedData = {
      id: unit?.id,
      title: unit?.title,
      base_occupancy: unit?.base_occupancy,
      max_occupancy: unit?.max_occupancy,
      category_id: unit?.category_id,
      description: unit?.description || "",
      amenities: unit?.amenities || null,
    };

    dispatch(showSuccess(t("Successfully updated unit type")));
    setUnitType(
      prevState => ({ ...(prevState || {}), ...updatedData } as IAccommodationUnitType & { description: string }),
    );
    setUnitTypeBeforeChanges(
      prevState => ({ ...(prevState || {}), ...updatedData } as IAccommodationUnitType & { description: string }),
    );
  }

  async function loadAccommodationCategories() {
    if (accommodationCategories !== undefined) {
      setAccommodationCategories(undefined);
    }
    const categoriesRes = await GetAccommodationCategories(null, false);

    if (categoriesRes.status !== StatusCode.OK) {
      setAccommodationCategories([]);
      return;
    }

    setAccommodationCategories(categoriesRes.data);
  }

  async function uploadUnitTypeImage(imageFile: File) {
    const formData = new FormData();

    formData.append("unit_type_id", String(typeId));
    formData.append("image", imageFile);

    const imageRes = await PostAccommodationUnitTypeImage(formData, true);

    console.log(imageRes);

    if (imageRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error uploading unit type image")));
      return;
    }

    void loadAccommodationUnitType();
  }

  async function deleteUnitTypeImage() {
    const formData = new FormData();

    formData.append("unit_type_id", String(typeId));
    formData.append("image", undefined);

    const imageRes = await PostAccommodationUnitTypeImage(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing unit type image")));
      return;
    }

    setFilterState(prev => ({ ...prev, deleteImageOpen: false }));
    setUnitTypeImages([]);
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterState({
      ...filterState,
      search: e.target.value,
      isDropdownOpen: true,
    });
  };

  const handleAddAmenity = (amenity: IAccommodationAmenity) => {
    setUnitType(prev => ({
      ...prev,
      amenities: [...(prev.amenities || []), amenity],
    }));
    setFilterState(prev => ({ ...prev, search: "", isDropdownOpen: false }));
  };

  const filteredAmenities =
    filterState?.search === "*"
      ? accommodationAmenities?.filter(amenity => !unitType?.amenities?.some(selected => selected.id === amenity.id))
      : accommodationAmenities
          ?.filter(amenity => amenity?.title?.toLowerCase()?.includes(filterState?.search?.toLowerCase()))
          ?.filter(amenity => !unitType?.amenities?.some(selected => selected.id === amenity?.id));

  return (
    <Page
      title={"Edit unit type"}
      subtitle={t("Enter the key details for your unit type")}
      breadcrumbs={[
        { label: t("Accommodations"), url: "/admin/accommodation" },
        { label: t("Property Configuration"), url: "/admin/accommodation/configuration/unit-types" },
        { label: t("Unit Types"), url: "/admin/accommodation/configuration/unit-types" },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(unitTypeBeforeChanges, unitType),
        onAction: () => updateAccommodationUnitType(),
        onCancel: () => setUnitType(unitTypeBeforeChanges),
      }}
    >
      <Card title="General Information">
        <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
          <hr style={{ marginBottom: "0px" }} />
        </div>

        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={unitType?.title}
                label={t("Title")}
                id="title"
                onChange={e => handleChangeEventInput(e, unitType, setUnitType)}
                placeholder={t("Unit Type Name")}
              />

              <Select
                label={t("Category")}
                onChange={(value: number) => setUnitType(prev => ({ ...prev, category_id: Number(value) }))}
                defaultValue={unitType?.category_id}
              >
                {accommodationCategories?.map((category: IAccommodationCategory) => (
                  <Select.Option key={category.id} value={category.id} name={category.title}>
                    {category.title}
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={unitType?.base_occupancy}
                label={t("Base Occupancy")}
                id="base_occupancy"
                onChange={e => handleChangeEventInput(e, unitType, setUnitType)}
                placeholder={"2"}
                type={"number"}
              />
              <Input
                value={unitType?.max_occupancy}
                label={t("Max Occupancy")}
                id="max_occupancy"
                onChange={e => handleChangeEventInput(e, unitType, setUnitType)}
                placeholder={"4"}
                type={"number"}
              />
            </FormLayout.Group>

            {/* <FormLayout.Group>
              <div>
                <TextField
                  label={t("Description")}
                  value={unitType?.description}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    setUnitType(prev => ({
                      ...prev,
                      description: e?.target?.value,
                    }))
                  }
                  maxLength={300}
                  characterCountBottom
                  rows={5}
                  placeholder="Enter unit type description"
                />
              </div>
            </FormLayout.Group> */}
          </FormLayout>
        </Card.Section>
      </Card>

      <Card title="Amenities">
        <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
          <hr style={{ marginBottom: "0px" }} />
        </div>

        <Card.Section>
          <div ref={amenitiesDropdownRef} style={{ position: "relative" }}>
            <Input
              label={t("Amenities")}
              id="amenities"
              value={filterState?.search}
              onChange={handleSearchChange}
              placeholder={t("Search and add amenities")}
              type="search"
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} color="base" />}
              onFocus={() => setFilterState(prev => ({ ...prev, isDropdownOpen: true }))}
            />

            {filterState.isDropdownOpen && filteredAmenities?.length > 0 && (
              <div className="amenities-dropdown">
                {filteredAmenities?.map(amenity => (
                  <div
                    key={amenity.id}
                    className="amenities-dropdown-item"
                    onClick={() => {
                      handleAddAmenity(amenity);
                    }}
                  >
                    <FontAwesomeIcon icon={allIcons[amenity?.icon]} style={{ fontSize: "18px", marginRight: "12px" }} />
                    {amenity?.title}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="amenities-selected-container">
            {unitType?.amenities?.map((amenity, index) => (
              <div key={index} className="amenities-selected-container_item">
                <FontAwesomeIcon icon={allIcons[amenity?.icon]} style={{ fontSize: "14px", marginRight: "3px" }} />
                {amenity?.title}{" "}
                <FontAwesomeIcon
                  icon={faXmark}
                  onClick={() =>
                    setUnitType(prev => ({
                      ...prev,
                      amenities: prev.amenities?.filter(a => a.id !== amenity.id),
                    }))
                  }
                  className="amenities-selected-container_item_remove"
                />
              </div>
            ))}
          </div>
        </Card.Section>
      </Card>
      <Card title="Upload Media">
        <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
          <hr style={{ marginBottom: "0px" }} />
        </div>

        <Card.Section>
          <ImageUpload
            image={unitTypeImages}
            onNew={img => void uploadUnitTypeImage(img)}
            onRemove={() => {
              setFilterState(prev => ({ ...prev, deleteImageOpen: true }));
            }}
            maxHeight={400}
            maxWidth={800}
          />
        </Card.Section>
      </Card>

      <Popup
        open={filterState.deleteImageOpen}
        onCancel={() => setFilterState(prev => ({ ...prev, deleteImageOpen: false }))}
        title="Delete Image"
        description="Are you sure you want to delete this image?"
        onOk={deleteUnitTypeImage}
        type="warning"
      />
    </Page>
  );
}
